import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/**
 * Sprint 58 — Dark mode.
 *
 * Tryby:
 * - `light` / `dark` — explicit wybór usera
 * - `system` — follow `prefers-color-scheme` (auto-switch gdy OS zmienia)
 *
 * Persist: localStorage (key `theme`). W przyszłości (Sprint 58.B) można
 * dopisać sync z UserSettings w DB żeby działało cross-device.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  /** Efektywny tryb (rozwiązany system→light/dark). Dla warunkowych renderów. */
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = 'theme';

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyClassToRoot(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // meta color-scheme dla natywnych UI (scrollbar, input autofill)
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => {
    const initial = readStoredMode();
    if (initial === 'system') return systemPrefersDark() ? 'dark' : 'light';
    return initial;
  });

  // Apply class initially + on mode change
  useEffect(() => {
    applyClassToRoot(resolved);
  }, [resolved]);

  // Watch system preference changes (only for 'system' mode)
  useEffect(() => {
    if (mode !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolved(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    if (next === 'system') {
      setResolved(systemPrefersDark() ? 'dark' : 'light');
    } else {
      setResolved(next);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

/**
 * Blocking script dla index.html — ustawia klasę `dark` PRZED hydratacją React'a,
 * żeby uniknąć "flash of wrong theme" (FOUC).
 * Embed w <head> jako inline <script>.
 */
export const THEME_INIT_SCRIPT = `
(function() {
  try {
    var v = localStorage.getItem('theme') || 'system';
    var isDark = v === 'dark' || (v === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.style.colorScheme = 'light';
    }
  } catch (e) {}
})();
`;
